import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, Download, UserPlus, Wallet, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { AgingKpiCards } from '../../components/recouvrement/AgingKpiCards';
import { TeamList } from '../../components/recouvrement/TeamList';
import { InvoiceTable } from '../../components/recouvrement/InvoiceTable';
import { InvoiceDrawer } from '../../components/recouvrement/InvoiceDrawer';
import { RelanceModal } from '../../components/recouvrement/RelanceModal';
import { fmtXof, BUCKET_ORDER } from '../../components/recouvrement/recMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  { value: 'call', label: 'Appel' }, { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' }, { value: 'in_person', label: 'Présentiel' }, { value: 'other', label: 'Autre' },
];
const OUTCOMES = [
  { value: 'promise_to_pay', label: 'Promesse de paiement' }, { value: 'partial_payment', label: 'Paiement partiel' },
  { value: 'paid_confirmed', label: 'Paiement confirmé' }, { value: 'no_reply', label: 'Pas de réponse' },
  { value: 'dispute', label: 'Litige / contestation' }, { value: 'callback_requested', label: 'Rappel demandé' },
];

export default function MyOverduePage() {
  const { user } = useAuth();
  const isLead = ['lead_commercial', 'admin', 'finance'].includes(user?.role);
  const canAssign = ['admin', 'lead_commercial'].includes(user?.role);

  const [view, setView] = useState('me'); // 'me' | 'team'
  const [meData, setMeData] = useState(null);
  const [team, setTeam] = useState(null);
  // L'admin (pas de portefeuille perso) atterrit sur la vue équipe
  useEffect(() => { if (user?.role === 'admin') setView('team'); }, [user?.role]);
  const [selected, setSelected] = useState(null); // {type:'commercial'|'unassigned', id, name}
  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agingFilter, setAgingFilter] = useState(null);
  const [drawerRef, setDrawerRef] = useState(null);

  // Dialogs (relance + assignation)
  const [relanceInv, setRelanceInv] = useState(null);
  const [emailRelanceRef, setEmailRelanceRef] = useState(null);
  const [form, setForm] = useState({ channel: 'call', outcome: 'promise_to_pay', promiseDate: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [unassigned, setUnassigned] = useState(null);
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [targetCommercial, setTargetCommercial] = useState('');
  const [assigning, setAssigning] = useState(false);

  const loadMe = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/commercials/me/overdue-invoices`); setMeData(data); }
    catch { toast.error('Impossible de charger les encours'); }
    finally { setLoading(false); }
  }, []);

  const loadTeam = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/commercials/team/overdue-summary`); setTeam(data); }
    catch { toast.error('Impossible de charger la vue équipe'); }
  }, []);

  useEffect(() => { loadMe(); if (isLead) loadTeam(); }, [loadMe, loadTeam, isLead]);

  // Charge le détail d'un membre / des non-assignés
  useEffect(() => {
    if (view !== 'team' || !selected) { setMemberData(null); return; }
    let cancel = false;
    setMemberData(null);
    const url = selected.type === 'unassigned'
      ? `${API}/admin/commercials/team/unassigned-invoices`
      : `${API}/admin/commercials/${selected.id}/overdue-invoices`;
    axios.get(url).then(({ data }) => { if (!cancel) setMemberData(data); }).catch(() => toast.error('Impossible de charger le détail'));
    return () => { cancel = true; };
  }, [view, selected]);

  const refreshAll = useCallback(() => {
    loadMe(); if (isLead) loadTeam();
    if (view === 'team' && selected) {
      const url = selected.type === 'unassigned'
        ? `${API}/admin/commercials/team/unassigned-invoices`
        : `${API}/admin/commercials/${selected.id}/overdue-invoices`;
      axios.get(url).then(({ data }) => setMemberData(data)).catch(() => {});
    }
  }, [loadMe, loadTeam, isLead, view, selected]);

  const openAssign = useCallback(async () => {
    setAssignOpen(true); setSelectedClients(new Set()); setTargetCommercial(team?.recommendedCommercialId || '');
    try { const { data } = await axios.get(`${API}/admin/commercials/team/unassigned-clients`); setUnassigned(data); }
    catch { toast.error('Impossible de charger les clients non assignés'); }
  }, [team]);

  const submitAssign = async () => {
    if (!targetCommercial || selectedClients.size === 0) { toast.error('Sélectionnez des clients et un commercial'); return; }
    setAssigning(true);
    try {
      const { data } = await axios.post(`${API}/admin/companies/bulk-assign-commercial`, {
        clientIds: Array.from(selectedClients), commercialId: targetCommercial, reason: 'Prise en charge recouvrement',
      });
      toast.success(`${data.updated} client(s) assigné(s) à ${data.commercialName}`);
      setAssignOpen(false); refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'assignation"); }
    finally { setAssigning(false); }
  };

  const assignClientInline = async (inv, commercialId) => {
    if (!inv.client_id) { toast.error('Client introuvable pour cette facture'); return; }
    try {
      await axios.patch(`${API}/admin/companies/${inv.client_id}/commercial`, { commercialId, reason: 'Affectation recouvrement' });
      toast.success('Client assigné');
      refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'assignation"); }
  };

  const submitRelance = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/commercials/dunning-actions`, {
        companyName: relanceInv.client_name, invoiceRef: relanceInv.invoice_ref,
        channel: form.channel, outcome: form.outcome, promiseDate: form.promiseDate || null, note: form.note || null,
      });
      toast.success('Relance enregistrée');
      setRelanceInv(null); setForm({ channel: 'call', outcome: 'promise_to_pay', promiseDate: '', note: '' });
      refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const exportCsv = () => {
    const scope = view === 'team' ? memberData : meData;
    const rows = [];
    BUCKET_ORDER.forEach((k) => (scope?.buckets?.[k] || []).forEach((inv) => rows.push(inv)));
    if (rows.length === 0) { toast.info('Aucune facture à exporter dans cette vue'); return; }
    const header = ['Reference', 'Client', 'Montant_du', 'Jours_retard', 'Relances', 'Statut'];
    const csv = [header.join(',')].concat(rows.map((r) => [
      r.invoice_ref, `"${(r.client_name || '').replace(/"/g, '""')}"`, r.remaining, r.days_overdue, r.reminder_count || 0, r.status || '',
    ].join(','))).join('\n');
    const url = window.URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `encours_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success('Export CSV généré');
  };

  const commercials = team?.commercials || [];

  // KPI scope (haut de page)
  const kpiData = useMemo(() => {
    if (view === 'team') return { bucketTotals: team?.totals?.bucketTotals || {}, bucketCounts: team?.totals?.bucketCounts || {} };
    return { bucketTotals: meData?.bucketTotals || {}, bucketCounts: meData?.bucketCounts || {} };
  }, [view, team, meData]);

  const headerTotal = view === 'team' ? (team?.totals?.totalOutstanding ?? 0) : (meData?.totalOutstanding ?? 0);
  const headerCount = view === 'team' ? (team?.totals?.count ?? 0) : (meData?.count ?? 0);

  if (loading) {
    return <div className="space-y-4" data-testid="overdue-loading">
      <Skeleton className="h-10 w-72" />
      <div className="grid grid-cols-4 gap-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
    </div>;
  }

  return (
    <div className={`space-y-5 transition-all ${drawerRef ? 'xl:pr-[460px]' : ''}`} data-testid="encours-recouvrement-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">Encours &amp; Recouvrement</h1>
          <p className="mt-1 text-sm text-gray-500">Suivez les impayés, les encours par commercial et les actions de relance depuis une seule vue.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isLead && (
            <div className="flex rounded-lg border border-gray-200 bg-white p-1" data-testid="rec-view-toggle">
              <Button size="sm" variant={view === 'me' ? 'default' : 'ghost'} className={`h-8 text-xs ${view === 'me' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`} onClick={() => { setView('me'); setSelected(null); }} data-testid="rec-view-me">Mon portefeuille</Button>
              <Button size="sm" variant={view === 'team' ? 'default' : 'ghost'} className={`h-8 text-xs ${view === 'team' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`} onClick={() => setView('team')} data-testid="rec-view-team">Vue équipe</Button>
            </div>
          )}
          <div className="text-right">
            <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Total encours</div>
            <div className="text-xl font-bold text-emerald-700 tabular-nums" data-testid="rec-total-outstanding">{fmtXof(headerTotal)}</div>
            <div className="text-xs text-gray-400">{headerCount} facture{headerCount > 1 ? 's' : ''}</div>
          </div>
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={exportCsv} data-testid="rec-export-btn"><Download className="mr-1.5 h-3.5 w-3.5" /> Exporter</Button>
          {canAssign && <Button size="sm" className="h-9 bg-emerald-600 text-xs hover:bg-emerald-700" onClick={openAssign} data-testid="rec-assign-unassigned-btn"><UserPlus className="mr-1.5 h-3.5 w-3.5" /> Assigner les non affectés</Button>}
        </div>
      </div>

      {/* KPI ancienneté */}
      <AgingKpiCards bucketTotals={kpiData.bucketTotals} bucketCounts={kpiData.bucketCounts} activeBucket={agingFilter} onSelect={setAgingFilter} />

      {/* Bandeau non-assignés */}
      {isLead && view === 'team' && team?.unassigned?.count > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3" data-testid="rec-unassigned-alert">
          <AlertTriangle className="h-5 w-5 shrink-0 text-orange-500" />
          <div className="flex-1 text-sm text-orange-900">
            <span className="font-semibold">{team.unassigned.count} factures</span> sont liées à des clients <span className="font-semibold">non assignés</span>, pour un total de {fmtXof(team.unassigned.totalOutstanding)}
            {team.unassigned.bucketTotals['90+'] > 0 && <>, dont {fmtXof(team.unassigned.bucketTotals['90+'])} à +90 jours</>}. Affectez-les à un commercial pour lancer le recouvrement.
          </div>
          {canAssign && <Button size="sm" className="h-8 bg-orange-500 text-xs text-white hover:bg-orange-600" onClick={openAssign} data-testid="rec-alert-assign-btn">Assigner maintenant</Button>}
        </div>
      )}

      {/* Layout principal */}
      {view === 'me' ? (
        meData && <InvoiceTable
          title="Mon portefeuille" subtitle={`${fmtXof(meData.totalOutstanding)} · ${meData.count} facture${meData.count > 1 ? 's' : ''}`}
          avatarKey={user?.id} data={meData} mode="member"
          onOpenInvoice={(inv) => setDrawerRef(inv.invoice_ref)} onRelance={setRelanceInv}
          onEmailRelance={(inv) => setEmailRelanceRef(inv.invoice_ref)}
          agingFilter={agingFilter} onAgingFilter={setAgingFilter}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
          <div className="xl:col-span-5">
            {team ? <TeamList team={team} selectedKey={selected?.id} onSelect={(s) => { setSelected(s); setAgingFilter(null); }} /> : <Skeleton className="h-96 rounded-xl" />}
          </div>
          <div className="xl:col-span-7">
            {!selected ? (
              <div className="flex h-full min-h-[320px] items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white" data-testid="rec-no-selection">
                <div className="text-center text-gray-400">
                  <Wallet className="mx-auto mb-2 h-10 w-10 text-gray-300" />
                  <p className="text-sm">Sélectionnez un commercial pour voir le détail de ses encours.</p>
                </div>
              </div>
            ) : !memberData ? (
              <Skeleton className="h-96 rounded-xl" />
            ) : (
              <InvoiceTable
                title={selected.type === 'unassigned' ? 'Clients non assignés' : selected.name}
                subtitle={`${fmtXof(memberData.totalOutstanding)} · ${memberData.count} facture${memberData.count > 1 ? 's' : ''}`}
                avatarKey={selected.id} data={memberData}
                mode={selected.type === 'unassigned' ? 'unassigned' : 'member'}
                commercials={commercials} recommendedCommercialId={team?.recommendedCommercialId}
                onOpenInvoice={(inv) => setDrawerRef(inv.invoice_ref)} onRelance={setRelanceInv}
                onEmailRelance={(inv) => setEmailRelanceRef(inv.invoice_ref)}
                onAssignClient={canAssign ? assignClientInline : undefined}
                agingFilter={agingFilter} onAgingFilter={setAgingFilter}
                headerActions={selected.type === 'unassigned' && canAssign
                  ? <Button size="sm" className="h-8 bg-emerald-600 text-xs hover:bg-emerald-700" onClick={openAssign} data-testid="rec-bulk-assign-btn">Assigner en lot</Button>
                  : null}
              />
            )}
          </div>
        </div>
      )}

      {/* Drawer facture */}
      {drawerRef && (
        <InvoiceDrawer invoiceRef={drawerRef} commercials={commercials} onClose={() => setDrawerRef(null)} onChanged={refreshAll} />
      )}

      {/* Dialog assignation (multi) */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-2xl" data-testid="rec-assign-dialog">
          <DialogHeader><DialogTitle>Assigner des clients non assignés</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Commercial cible {team?.recommendedCommercialId && <span className="text-xs font-normal text-emerald-600">· recommandé pré-sélectionné</span>}</label>
              <Select value={targetCommercial} onValueChange={setTargetCommercial}>
                <SelectTrigger data-testid="rec-assign-commercial-select"><SelectValue placeholder="Choisir un commercial…" /></SelectTrigger>
                <SelectContent>
                  {commercials.map((c) => (
                    <SelectItem key={c.commercialId} value={c.commercialId}>{c.commercialName} ({c.portfolioSize ?? 0} cl.){c.commercialId === team?.recommendedCommercialId ? ' · Recommandé' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="max-h-72 overflow-y-auto rounded-lg border">
              {!unassigned ? <div className="p-4 text-sm text-gray-500">Chargement…</div>
                : unassigned.clients.length === 0 ? <div className="p-4 text-sm text-gray-500">Aucun client non assigné avec encours.</div>
                  : (
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-600">
                        <tr>
                          <th className="w-10 p-2"><Checkbox checked={selectedClients.size === unassigned.clients.length && unassigned.clients.length > 0} onCheckedChange={(v) => setSelectedClients(v ? new Set(unassigned.clients.map((c) => c.clientId)) : new Set())} data-testid="rec-assign-select-all" /></th>
                          <th className="p-2 text-left">Client</th>
                          <th className="p-2 text-right">Encours</th>
                          <th className="p-2 text-center">Retard max</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unassigned.clients.map((c) => (
                          <tr key={c.clientId} className="border-t hover:bg-gray-50" data-testid={`rec-assign-client-${c.clientId}`}>
                            <td className="p-2"><Checkbox checked={selectedClients.has(c.clientId)} onCheckedChange={(v) => { const n = new Set(selectedClients); if (v) n.add(c.clientId); else n.delete(c.clientId); setSelectedClients(n); }} data-testid={`rec-assign-check-${c.clientId}`} /></td>
                            <td className="p-2 font-medium">{c.companyName}</td>
                            <td className="p-2 text-right font-semibold text-red-600">{fmtXof(c.totalOutstanding)}</td>
                            <td className="p-2 text-center">{c.maxDaysOverdue > 0 ? `${c.maxDaysOverdue} j` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} data-testid="rec-assign-cancel">Annuler</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submitAssign} disabled={assigning || !targetCommercial || selectedClients.size === 0} data-testid="rec-assign-submit">
              {assigning ? 'Assignation…' : `Assigner ${selectedClients.size > 0 ? `(${selectedClients.size})` : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {emailRelanceRef && (
        <RelanceModal
          open={!!emailRelanceRef}
          invoiceRef={emailRelanceRef}
          channel="email"
          onClose={() => setEmailRelanceRef(null)}
          onSent={refreshAll}
        />
      )}

      {/* Dialog relance */}      <Dialog open={!!relanceInv} onOpenChange={(o) => !o && setRelanceInv(null)}>
        <DialogContent data-testid="rec-relance-dialog">
          <DialogHeader><DialogTitle>Logger une relance</DialogTitle></DialogHeader>
          {relanceInv && (
            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
                <div className="font-medium">{relanceInv.client_name}</div>
                <div className="text-xs">Facture {relanceInv.invoice_ref} · {fmtXof(relanceInv.remaining)} · {relanceInv.days_overdue}j retard</div>
              </div>
              <div>
                <label className="text-sm font-medium">Canal</label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger data-testid="rec-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CHANNELS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Résultat</label>
                <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
                  <SelectTrigger data-testid="rec-outcome-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{OUTCOMES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.outcome === 'promise_to_pay' && (
                <div><label className="text-sm font-medium">Date promise</label><Input type="date" value={form.promiseDate} onChange={(e) => setForm({ ...form, promiseDate: e.target.value })} data-testid="rec-promise-date" /></div>
              )}
              <div><label className="text-sm font-medium">Note</label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Détails de l'échange…" data-testid="rec-relance-note" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRelanceInv(null)} data-testid="rec-relance-cancel">Annuler</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submitRelance} disabled={saving} data-testid="rec-relance-submit">{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
